import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

import {
  transition,
  type ReplaySessionState,
} from "@/lib/lytick/replayStateMachine";
import {
  normalizeCandles,
  normalizeTimestampMs,
  type Candle,
} from "@/lib/lytick/replay-utils";
import { searchTimeNearest, candleAt } from "@/lib/lytick/columnar";
import { getBaseColumns, getDatasetVersion } from "@/lib/lytick/chart-data-service";
import { useChartStore } from "@/stores/chartStore";
import { useReplayResumeStore } from "@/stores/replayResumeStore";

export type ReplaySpeed = 0.5 | 1 | 2 | 5 | 10 | 20 | 50;
export type ReplayBackfillStatus = "idle" | "loading" | "ready" | "error";

let preservedSpeed: ReplaySpeed | null = null;

interface ReplayState {
  sessionState: ReplaySessionState;
  errorMessage: string | null;

  /** Version counter from chart-data-service; incremented on each new dataset load. */
  masterColumnsVersion: number;
  /** Pre-replay context candles fetched at display-timeframe granularity. */
  contextCandles: Candle[];
  contextGranularitySeconds: number;
  cursorTimestamp: number | null;
  anchorIndex: number;
  position: number;
  totalCandles: number;
  sourceGranularitySeconds: number;

  speed: ReplaySpeed;
  startDate: number | null;
  endDate: number | null;

  backfillStatus: ReplayBackfillStatus;
  oldestLoadedEpoch: number | null;
  latestLoadedEpoch: number | null;
  hasReachedDepthCeiling: boolean;

  currentCandle: Candle | null;
  progress: number;
  isActive: boolean;
  isPaused: boolean;
  isLoading: boolean;
  loadProgress: number;

  isReplayMode: boolean;
  startLineTimestamp: number | null;
  isStartLineLocked: boolean;
  contextCandleCount: number;

  endBannerVisible: boolean;
  blindMode: boolean;

  transitionTo: (targetState: ReplaySessionState) => void;
  startPreflight: (startDate: number, endDate: number) => void;
  cancelPreflight: () => void;
  setReplayData: (
    startDate: number,
    endDate: number,
    sourceGranularitySeconds?: number,
  ) => void;
  stopReplay: () => void;
  jumpToLive: () => void;
  play: () => void;
  pause: () => void;
  togglePause: () => void;
  setSpeed: (speed: ReplaySpeed) => void;
  stepForward: () => void;
  stepForwardBy: (count: number) => void;
  stepBackward: () => void;
  jumpToStart: () => void;
  jumpToEnd: () => void;
  setPosition: (position: number) => void;
  seekToTimestamp: (epochMs: number) => void;
  setLoading: (loading: boolean) => void;
  setLoadProgress: (progress: number) => void;
  setBackfillStatus: (status: ReplayBackfillStatus) => void;
  setHasReachedDepthCeiling: (value: boolean) => void;
  setErrorMessage: (message: string | null) => void;
  /** No-op — CSV data is fully loaded at import time. */
  prependHistoryCandles: (candles: Candle[]) => number;
  /** No-op — CSV data is fully loaded at import time. */
  appendFutureCandles: (candles: Candle[]) => number;
  setContextCandles: (candles: Candle[], granularitySeconds: number) => void;
  refreshContextAtGranularity: (granularitySeconds: number) => void;
  clearRefreshingContext: () => void;
  isRefreshingContext: boolean;

  enterReplayMode: () => void;
  exitReplayMode: () => void;
  lockStartLine: () => void;
  setStartLineTimestamp: (timestamp: number | null) => void;
  setContextCandleCount: (count: number) => void;

  showEndBanner: () => void;
  dismissEndBanner: () => void;
  toggleBlindMode: () => void;
}

type ReplayStateSnapshot = Omit<
  ReplayState,
  | "startPreflight"
  | "cancelPreflight"
  | "setReplayData"
  | "stopReplay"
  | "jumpToLive"
  | "transitionTo"
  | "play"
  | "pause"
  | "togglePause"
  | "setSpeed"
  | "stepForward"
  | "stepForwardBy"
  | "stepBackward"
  | "jumpToStart"
  | "jumpToEnd"
  | "setPosition"
  | "seekToTimestamp"
  | "setLoading"
  | "setLoadProgress"
  | "setBackfillStatus"
  | "setHasReachedDepthCeiling"
  | "setErrorMessage"
  | "prependHistoryCandles"
  | "appendFutureCandles"
  | "setContextCandles"
  | "refreshContextAtGranularity"
  | "clearRefreshingContext"
  | "enterReplayMode"
  | "exitReplayMode"
  | "lockStartLine"
  | "setStartLineTimestamp"
  | "setContextCandleCount"
  | "showEndBanner"
  | "dismissEndBanner"
  | "toggleBlindMode"
  | "setReplayLineColor"
>;

// ─── Helpers reading from chart-data-service ──────────────────────────────────

function resolvePositionFromCursor(cursorTimestamp: number | null): number {
  const base = getBaseColumns();
  if (cursorTimestamp == null || !base || base.length === 0) return 0;
  return searchTimeNearest(base, cursorTimestamp);
}

function getTotalCandles(): number {
  return getBaseColumns()?.length ?? 0;
}

function timestampAt(pos: number): number | null {
  const base = getBaseColumns();
  if (!base || pos < 0 || pos >= base.length) return null;
  return base.time[pos];
}

function candleAtPos(pos: number): Candle | null {
  const base = getBaseColumns();
  if (!base || pos < 0 || pos >= base.length) return null;
  return candleAt(base, pos);
}

// ─── State derivation ─────────────────────────────────────────────────────────

function deriveReplayState(state: ReplayStateSnapshot): ReplayStateSnapshot {
  const totalCandles = getTotalCandles();
  const position = totalCandles === 0
    ? 0
    : resolvePositionFromCursor(state.cursorTimestamp);
  const anchorIndex =
    totalCandles === 0
      ? 0
      : Math.max(0, Math.min(state.anchorIndex, totalCandles - 1));
  const currentCandle = candleAtPos(position);
  const progressDenominator = Math.max(1, totalCandles - 1 - anchorIndex);
  const progress =
    totalCandles <= 1
      ? 0
      : Math.max(0, Math.min(1, (position - anchorIndex) / progressDenominator));

  return {
    ...state,
    anchorIndex,
    position,
    totalCandles,
    currentCandle,
    progress,
    isActive: state.sessionState !== "live",
    isPaused: state.sessionState === "paused",
    isLoading: state.sessionState === "loading",
  };
}

function createBaseSnapshot(): ReplayStateSnapshot {
  return deriveReplayState({
    sessionState: "live",
    errorMessage: null,
    masterColumnsVersion: 0,
    contextCandles: [],
    contextGranularitySeconds: 86400,
    cursorTimestamp: null,
    anchorIndex: 0,
    position: 0,
    totalCandles: 0,
    sourceGranularitySeconds: 60,
    speed: 1,
    startDate: null,
    endDate: null,
    backfillStatus: "idle",
    oldestLoadedEpoch: null,
    latestLoadedEpoch: null,
    hasReachedDepthCeiling: false,
    currentCandle: null,
    progress: 0,
    isActive: false,
    isPaused: true,
    isLoading: false,
    loadProgress: 0,
    isReplayMode: false,
    startLineTimestamp: null,
    isStartLineLocked: false,
    contextCandleCount: 50,
    endBannerVisible: false,
    isRefreshingContext: false,
    blindMode: false,
  });
}

function buildReplayDataset(
  startDate: number,
  endDate: number,
  speed: ReplaySpeed,
  sourceGranularitySeconds = 60,
): ReplayStateSnapshot {
  const base = getBaseColumns();
  const normalizedStartDate = normalizeTimestampMs(startDate);
  const normalizedEndDate = normalizeTimestampMs(endDate);
  const totalCandles = base?.length ?? 0;

  const anchorIndex =
    normalizedStartDate == null || !base
      ? 0
      : searchTimeNearest(base, normalizedStartDate);

  const cursorTimestamp = timestampAt(anchorIndex) ?? normalizedStartDate;

  return deriveReplayState({
    sessionState: "ready",
    errorMessage: null,
    masterColumnsVersion: getDatasetVersion(),
    contextCandles: [],
    contextGranularitySeconds: 86400,
    cursorTimestamp,
    anchorIndex,
    position: anchorIndex,
    totalCandles,
    sourceGranularitySeconds,
    speed,
    startDate: normalizedStartDate,
    endDate: normalizedEndDate,
    backfillStatus: "idle",
    oldestLoadedEpoch: timestampAt(0),
    latestLoadedEpoch: timestampAt(Math.max(0, totalCandles - 1)),
    hasReachedDepthCeiling: false,
    currentCandle: candleAtPos(anchorIndex),
    progress: 0,
    isActive: false,
    isPaused: false,
    isLoading: false,
    loadProgress: 100,
    isReplayMode: false,
    startLineTimestamp: null,
    isStartLineLocked: false,
    contextCandleCount: 50,
    endBannerVisible: false,
    isRefreshingContext: false,
    blindMode: false,
  });
}

function transitionState(
  state: ReplayStateSnapshot,
  targetState: ReplaySessionState,
): ReplayStateSnapshot {
  const result = transition(state.sessionState, targetState);
  if (!result.ok) {
    return deriveReplayState({ ...state, errorMessage: result.error });
  }
  return deriveReplayState({
    ...state,
    sessionState: result.state,
    errorMessage: targetState === "error" ? state.errorMessage : null,
  });
}

// ─── Store ────────────────────────────────────────────────────────────────────

export const useReplayStore = create<ReplayState>()(
  persist(
    (set, get) => ({
      ...createBaseSnapshot(),

  transitionTo: (targetState) =>
    set((state) => transitionState(state, targetState)),

  startPreflight: (startDate, endDate) =>
    set((state) =>
      transitionState(
        deriveReplayState({
          ...state,
          speed: preservedSpeed ?? state.speed,
          startDate: normalizeTimestampMs(startDate),
          endDate: normalizeTimestampMs(endDate),
          sourceGranularitySeconds: 60,
          loadProgress: 0,
          endBannerVisible: false,
          errorMessage: null,
        }),
        "loading",
      ),
    ),

  cancelPreflight: () =>
    set((state) => transitionState(state, "live")),

  setReplayData: (startDate, endDate, sourceGranularitySeconds) =>
    set((state) => ({
      ...buildReplayDataset(
        startDate,
        endDate,
        preservedSpeed ?? state.speed,
        sourceGranularitySeconds ?? 60,
      ),
      blindMode: state.blindMode,
    })),

  stopReplay: () => { preservedSpeed = null; set(() => createBaseSnapshot()); },

  jumpToLive: () => { preservedSpeed = null; set(() => createBaseSnapshot()); },

  play: () =>
    set((state) => {
      if (state.totalCandles === 0) {
        return deriveReplayState({ ...state, errorMessage: "Cannot start replay without candles" });
      }
      if (state.sessionState === "ended") {
        return deriveReplayState({ ...state, errorMessage: "Replay has ended. Restart or go live." });
      }
      return transitionState(state, "playing");
    }),

  pause: () =>
    set((state) => transitionState(state, "paused")),

  togglePause: () => {
    const state = get();
    if (!state.isActive) return;
    if (state.sessionState === "playing") { get().pause(); return; }
    if (state.sessionState === "ready" || state.sessionState === "paused") { get().play(); }
  },

  setSpeed: (speed) => {
    preservedSpeed = speed;
    set((state) => deriveReplayState({ ...state, speed }));
  },

  stepForward: () =>
    set((state) => {
      if (!state.isActive || state.totalCandles === 0) return state;
      const pos = resolvePositionFromCursor(state.cursorTimestamp);
      if (pos >= state.totalCandles - 1) {
        return state.sessionState === "playing"
          ? transitionState(state, "ended")
          : state;
      }
      const nextPos = Math.min(pos + 1, state.totalCandles - 1);
      const nextTimestamp = timestampAt(nextPos);
      if (nextTimestamp == null) return state;
      const reachedEnd = nextPos >= state.totalCandles - 1;
      return deriveReplayState({
        ...state,
        cursorTimestamp: nextTimestamp,
        sessionState: reachedEnd && state.sessionState === "playing" ? "ended" : state.sessionState,
      });
    }),

  stepForwardBy: (count) =>
    set((state) => {
      if (!state.isActive || state.totalCandles === 0) return state;
      const pos = resolvePositionFromCursor(state.cursorTimestamp);
      const nextPos = Math.min(pos + count, state.totalCandles - 1);
      const nextTimestamp = timestampAt(nextPos) ?? state.cursorTimestamp;
      const reachedEnd = nextPos >= state.totalCandles - 1;
      return deriveReplayState({
        ...state,
        cursorTimestamp: nextTimestamp,
        sessionState: reachedEnd && state.sessionState === "playing" ? "ended" : state.sessionState,
      });
    }),

  stepBackward: () =>
    set((state) => {
      if (!state.isActive || state.totalCandles === 0) return state;
      const pos = resolvePositionFromCursor(state.cursorTimestamp);
      if (pos <= 0) return state;
      const prevPos = Math.max(pos - 1, 0);
      const prevTimestamp = timestampAt(prevPos);
      if (prevTimestamp == null) return state;
      return deriveReplayState({
        ...state,
        cursorTimestamp: prevTimestamp,
        sessionState: state.sessionState === "ended" ? "paused" : state.sessionState,
      });
    }),

  jumpToStart: () =>
    set((state) => {
      const ts = timestampAt(state.anchorIndex) ?? state.cursorTimestamp;
      return deriveReplayState({
        ...state,
        cursorTimestamp: ts,
        sessionState: state.sessionState === "ended" ? "paused" : state.sessionState,
      });
    }),

  jumpToEnd: () =>
    set((state) => {
      const ts = timestampAt(Math.max(0, state.totalCandles - 1)) ?? state.cursorTimestamp;
      return deriveReplayState({
        ...state,
        cursorTimestamp: ts,
        sessionState: state.sessionState === "playing" ? "paused" : state.sessionState,
      });
    }),

  setPosition: (position) =>
    set((state) => {
      const clamped = Math.max(0, Math.min(position, state.totalCandles - 1));
      const ts = timestampAt(clamped) ?? state.cursorTimestamp;
      return deriveReplayState({
        ...state,
        cursorTimestamp: ts,
        sessionState: state.sessionState === "ended" ? "paused" : state.sessionState,
      });
    }),

  seekToTimestamp: (epochMs) =>
    set((state) =>
      deriveReplayState({
        ...state,
        cursorTimestamp: epochMs,
        sessionState: state.sessionState === "ended" ? "paused" : state.sessionState,
      }),
    ),

  setLoading: (loading) =>
    set((state) => {
      if (loading) {
        return transitionState(deriveReplayState({ ...state, errorMessage: null }), "loading");
      }
      if (state.sessionState !== "loading") return state;
      return deriveReplayState({
        ...state,
        sessionState: state.totalCandles > 0 ? "ready" : "live",
      });
    }),

  setLoadProgress: (progress) =>
    set((state) =>
      deriveReplayState({ ...state, loadProgress: Math.max(0, Math.min(100, progress)) }),
    ),

  setBackfillStatus: (status) =>
    set((state) => deriveReplayState({ ...state, backfillStatus: status })),

  setHasReachedDepthCeiling: (value) =>
    set((state) => deriveReplayState({ ...state, hasReachedDepthCeiling: value })),

  setErrorMessage: (message) =>
    set((state) =>
      deriveReplayState({
        ...state,
        sessionState: message ? "error" : state.sessionState,
        errorMessage: message,
      }),
    ),

  // CSV data is fully loaded at import; these are no-ops for the CSV-only path.
  prependHistoryCandles: () => 0,
  appendFutureCandles: () => 0,

  setContextCandles: (candles, granularitySeconds) =>
    set((state) =>
      deriveReplayState({
        ...state,
        contextCandles: normalizeCandles(candles),
        contextGranularitySeconds: Math.max(60, Math.floor(granularitySeconds)),
      }),
    ),

  refreshContextAtGranularity: (_granularitySeconds) =>
    set((state) => deriveReplayState({ ...state, isRefreshingContext: true })),

  clearRefreshingContext: () =>
    set((state) => deriveReplayState({ ...state, isRefreshingContext: false })),

  enterReplayMode: () => {
    const tf = useChartStore.getState().timeframe;
    useReplayResumeStore.getState().setEntryTimeframe(tf);
    set((state) => deriveReplayState({ ...state, isReplayMode: true, isStartLineLocked: false, startLineTimestamp: null }));
  },

  exitReplayMode: () =>
    set((state) => deriveReplayState({ ...state, isReplayMode: false, startLineTimestamp: null, isStartLineLocked: false })),

  setStartLineTimestamp: (timestamp) =>
    set((state) => deriveReplayState({ ...state, startLineTimestamp: timestamp })),

  lockStartLine: () =>
    set((state) => deriveReplayState({ ...state, isStartLineLocked: true })),

  setContextCandleCount: (count) =>
    set((state) => deriveReplayState({
      ...state,
      contextCandleCount: Math.max(10, Math.min(500, Math.round(count / 10) * 10)),
    })),

  showEndBanner: () =>
    set((state) => deriveReplayState({ ...state, endBannerVisible: true })),

  dismissEndBanner: () =>
    set((state) => deriveReplayState({ ...state, endBannerVisible: false })),

  toggleBlindMode: () =>
    set((state) => ({ ...state, blindMode: !state.blindMode })),
    }),
    {
      name: "lytick-replay-session",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => {
        const result: Record<string, unknown> = {
          cursorTimestamp: state.cursorTimestamp,
          startDate: state.startDate,
          endDate: state.endDate,
          speed: state.speed,
          sourceGranularitySeconds: state.sourceGranularitySeconds,
          isReplayMode: state.isReplayMode,
          isStartLineLocked: state.isStartLineLocked,
          startLineTimestamp: state.startLineTimestamp,
          sessionState: state.sessionState,
          contextGranularitySeconds: state.contextGranularitySeconds,
          contextCandleCount: state.contextCandleCount,
          blindMode: state.blindMode,
        };
        if (state.contextCandles.length <= 5000) {
          result.contextCandles = state.contextCandles;
        }
        return result;
      },
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Always land on a clean live chart after refresh — never auto-resume
          // placement mode or an in-progress replay session.
          state.sessionState = "live";
          state.isActive = false;
          state.isPaused = true;
          state.isReplayMode = false;
          state.isStartLineLocked = false;
          state.startLineTimestamp = null;
          state.cursorTimestamp = null;
        }
      },
    }
  )
);
